/**
 * Chain and token address constants
 * Pure constants with minimal external dependencies
 */
import { SOLANA_CHAIN_ID } from "@repo/types";
import { defineChain } from "viem";
import {
  arbitrum,
  base,
  blast,
  bsc,
  type Chain,
  linea,
  mainnet,
  monad,
  optimism,
  plumeMainnet,
  polygon,
  unichain,
} from "viem/chains";

/**
 * Robinhood Chain — Arbitrum-Orbit L2. viem ships no definition for it, so we
 * define it here as the single shared chain object imported across the monorepo
 * (config, indexer, webapps).
 *
 * MAINNET: chainId 4663 (`0x1237`), native gas token ETH. RPC default is the
 * public blockscout eth-rpc (key-free); a dedicated RPC is supplied via env
 * (JSON_RPC_URL_4663 / RPC_EIP155_4663_HTTPS). Explorer + canonical multicall3
 * verified on-chain.
 */
export const ROBINHOOD_CHAIN_ID = 4663 as const;
// NOTE: do not annotate as `: Chain` — that widens `.id` to `number` and
// cascades into ALL_CHAIN_IDS_TYPE (used as literal-union record keys in
// @repo/viem), breaking downstream typing. Let defineChain infer `id: 4663`.
export const robinhoodChain = defineChain({
  id: ROBINHOOD_CHAIN_ID,
  name: "Robinhood Chain",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://robinhoodchain.blockscout.com/api/eth-rpc"] },
  },
  blockExplorers: {
    default: {
      name: "Robinhood Explorer",
      url: "https://robinhoodchain.blockscout.com",
    },
  },
  contracts: {
    multicall3: { address: "0xcA11bde05977b3631167028862bE2a173976CA11" },
  },
  testnet: false,
});

/**
 * HyperEVM — the EVM of the Hyperliquid L1. Defined here (rather than importing
 * viem/chains' `hyperEvm`, which only newer viem versions ship) so every
 * package shares ONE chain object with a literal `id: 999` regardless of its
 * resolved viem version.
 *
 * MAINNET: chainId 999 (`0x3e7`), native gas token HYPE (18dp). Dual-block
 * architecture: ~1s small blocks (3M gas) + ~60s big blocks (30M gas) — a
 * transaction over 3M gas only fits a big block, so UserOperation bundles must
 * stay under 3M gas. RPC default is the key-free official endpoint; a dedicated
 * RPC is supplied via env (JSON_RPC_URL_999 / RPC_EIP155_999_HTTPS). Explorer
 * is a first-party Etherscan instance. Canonical multicall3 verified on-chain.
 */
export const HYPEREVM_CHAIN_ID = 999 as const;
// NOTE: do not annotate as `: Chain` — that widens `.id` to `number` and
// cascades into ALL_CHAIN_IDS_TYPE (used as literal-union record keys in
// @repo/viem), breaking downstream typing. Let defineChain infer `id: 999`.
export const hyperevmChain = defineChain({
  id: HYPEREVM_CHAIN_ID,
  name: "HyperEVM",
  nativeCurrency: { name: "HYPE", symbol: "HYPE", decimals: 18 },
  rpcUrls: {
    default: { http: ["https://rpc.hyperliquid.xyz/evm"] },
  },
  blockExplorers: {
    default: {
      name: "HyperEVMScan",
      url: "https://hyperevmscan.io",
    },
  },
  contracts: {
    multicall3: { address: "0xcA11bde05977b3631167028862bE2a173976CA11" },
  },
  testnet: false,
});

/**
 * Monad — parallel-EVM L1. viem 2.52.0 ships the chain natively, so re-export
 * it as the single shared chain object (unlike robinhood/hyperevm above, which
 * predate their viem definitions).
 *
 * MAINNET: chainId 143 (`0x8f`), native gas token MON (18dp). ~400ms blocks —
 * multiple blocks can share one timestamp, so ordering must use block/tx/log
 * position, never timestamps. Async execution: charged cost is
 * gasPrice × gasLimit (not gasUsed), 30M per-tx gas cap, and only
 * `debug_traceBlockByNumber` is available (no `trace_block`). RPC default is
 * the key-free official endpoint; a dedicated RPC is supplied via env
 * (JSON_RPC_URL_143 / RPC_EIP155_143_HTTPS). Canonical multicall3 verified
 * on-chain (2026-08-07).
 */
export const MONAD_CHAIN_ID = 143 as const;
// NOTE: keep viem's inferred type — annotating as `: Chain` widens `.id` to
// `number` and breaks the ALL_CHAIN_IDS_TYPE literal union in @repo/viem.
export const monadChain = monad;

export const NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS =
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee" as const;

export const ZERO_ADDRESS =
  "0x0000000000000000000000000000000000000000" as const;

/**
 * Supported OP-stack mainnets whose receipts carry an authoritative L1 data
 * fee component. Keep fee-accounting and verification policy on this shared
 * classification instead of scattering chain literals across consumers.
 */
export const OP_STACK_CHAIN_REFS: ReadonlySet<string> = new Set([
  optimism.id.toString(10),
  base.id.toString(10),
  blast.id.toString(10),
  unichain.id.toString(10),
]);

export const isOpStackChainRef = (chainRef: string | number): boolean =>
  OP_STACK_CHAIN_REFS.has(chainRef.toString(10));

// Wrapped native token addresses per chain
const WETH_ARBITRUM_ADDRESS = "0x82af49447d8a07e3bd95bd0d56f35241523fbab1";
const WETH_BASE_ADDRESS = "0x4200000000000000000000000000000000000006";
const WETH_POLYGON_ADDRESS = "0x7ceb23fd6bc0add59e62ac25578270cff1b9f619";
const WMATIC_POLYGON_ADDRESS = "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270";
const WETH_OPTIMISM_ADDRESS = "0x4200000000000000000000000000000000000006";
const WETH_BLAST_ADDRESS = "0x4300000000000000000000000000000000000004";
const WETH_MAINNET_ADDRESS = "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2";
const WETH_LINEA_ADDRESS = "0xe5D7C2a44FfDDf6b295A15c148167daaAf5Cf34f";
const WETH_UNICHAIN_ADDRESS = "0x4200000000000000000000000000000000000006";
const WBNB_BSC_ADDRESS = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";
export const WSOL_SOLANA_ADDRESS =
  "So11111111111111111111111111111111111111112";
const WPLUME_PLUME_ADDRESS = "0xEa237441c92CAe6FC17Caaf9a7acB3f953be4bd1";
// Robinhood Chain (mainnet 4663) WETH (wrapped native). Native gas token is ETH.
const WETH_ROBINHOOD_ADDRESS = "0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73";
// HyperEVM (mainnet 999) WHYPE — wrapped native HYPE, 18dp.
const WHYPE_HYPEREVM_ADDRESS = "0x5555555555555555555555555555555555555555";
// Monad (mainnet 143) WMON — canonical wrapped native MON, 18dp. Verified
// on-chain (symbol/name/decimals) and cross-checked against MonadScan and the
// LiFi curated token list (2026-08-07).
const WMON_MONAD_ADDRESS = "0x3bd359C1119dA7Da1D913D1C4D2B7c461115433A";

/**
 * Get the wrapped native token address for a given chain ID
 */
export const getWrappedNativeTokenAddressForChainId = (
  chainId: string | number,
): string => {
  switch (chainId.toString(10)) {
    case "137": // Polygon
      return WMATIC_POLYGON_ADDRESS;
    case "8453": // Base
      return WETH_BASE_ADDRESS;
    case "42161": // Arbitrum
      return WETH_ARBITRUM_ADDRESS;
    case "10": // Optimism
      return WETH_OPTIMISM_ADDRESS;
    case "81457": // Blast
      return WETH_BLAST_ADDRESS;
    case "1": // Mainnet
      return WETH_MAINNET_ADDRESS;
    case "59144": // Linea
      return WETH_LINEA_ADDRESS;
    case "130": // Unichain
      return WETH_UNICHAIN_ADDRESS;
    case "56": // BNB Chain
      return WBNB_BSC_ADDRESS;
    case String(SOLANA_CHAIN_ID): // Solana
      return WSOL_SOLANA_ADDRESS;
    case "98866": // Plume
      return WPLUME_PLUME_ADDRESS;
    case "4663": // Robinhood Chain
      return WETH_ROBINHOOD_ADDRESS;
    case "999": // HyperEVM
      return WHYPE_HYPEREVM_ADDRESS;
    case "143": // Monad
      return WMON_MONAD_ADDRESS;
    default:
      throw new Error(`Unsupported chainId: ${chainId}`);
  }
};

/**
 * Check if an address is the native token placeholder
 */
export const isAddressNativeTokenPlaceholder = (
  addy: `0x${string}`,
): boolean => {
  return (
    addy.toLowerCase() === NATIVE_TOKEN_INTERNAL_PLACEHOLDER_ADDRESS ||
    addy.toLowerCase() === ZERO_ADDRESS
  );
};

/**
 * Get the Chain object for a given chain ID
 */
export const getChainForChainId = (chainId: string | number): Chain => {
  switch (chainId.toString(10)) {
    case polygon.id.toString(10):
      return polygon;
    case base.id.toString(10):
      return base;
    case arbitrum.id.toString(10):
      return arbitrum;
    case optimism.id.toString(10):
      return optimism;
    case blast.id.toString(10):
      return blast;
    case mainnet.id.toString(10):
      return mainnet;
    case linea.id.toString(10):
      return linea;
    case unichain.id.toString(10):
      return unichain;
    case bsc.id.toString(10):
      return bsc;
    case plumeMainnet.id.toString(10):
      return plumeMainnet;
    case robinhoodChain.id.toString(10):
      return robinhoodChain;
    case hyperevmChain.id.toString(10):
      return hyperevmChain;
    case monadChain.id.toString(10):
      return monadChain;
    default:
      throw new Error(`Unsupported chainId: ${chainId}`);
  }
};
